import SlideOver from "@components/ui/SlideOver";
import Button from "@components/ui/Button";
import Spinner from "@components/ui/Spinner";
import ErrorBanner from "@components/shared/ErrorBanner";
import Table, {
  TableBody, TableCell, TableHead, TableHeaderCell, TableRow,
} from "@components/ui/Table";
import {
  CAMPAIGN_STATUS, OUTCOME_LABELS, SLOT_LABELS, SMS_STATUS,
  attemptLabel, formatWireTime, modeLabel, outcomeClassName, outcomeLabel,
} from "@components/scheduling/campaignLabels";

function StatusPill({ status }) {
  const meta = CAMPAIGN_STATUS[status] || { label: status, className: "bg-gray-100 text-gray-700" };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${meta.className}`}>
      {meta.label}
    </span>
  );
}

/** One campaign: what it is doing now, and every patient in it. */
export default function CampaignDetailDrawer({
  detail, loading, onClose, onControl, controlError, onDismissControlError,
}) {
  const campaign = detail?.campaign;
  const isRunning = campaign?.status === "running";
  const isPaused = campaign?.status === "paused";
  const canCancel = isRunning || isPaused;

  const controls = campaign && canCancel ? (
    <div className="flex items-center gap-2">
      {isRunning && (
        <Button color="light" onClick={() => onControl("pause", campaign.id)}>Pause</Button>
      )}
      {isPaused && (
        <Button color="primary" onClick={() => onControl("resume", campaign.id)}>Resume</Button>
      )}
      {canCancel && (
        <Button color="amber" onClick={() => onControl("cancel", campaign.id)}>
          Cancel campaign
        </Button>
      )}
    </div>
  ) : null;

  // A refused control has to be reported here: this drawer is an opaque
  // overlay over the page, so the tab's banner would never be seen. The
  // footer renders for the message alone when the campaign has since moved
  // to a state that offers no controls.
  const footer = controls || controlError ? (
    <div className="space-y-3">
      {controlError && (
        <ErrorBanner message={controlError} onDismiss={onDismissControlError} />
      )}
      {controls}
    </div>
  ) : null;

  return (
    <SlideOver open={Boolean(detail)} onClose={onClose}
               title={campaign?.name || "Campaign"} footer={footer} widthClass="max-w-4xl">
      {loading && !detail ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : campaign ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <StatusPill status={campaign.status} />
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
              {modeLabel(campaign)}
            </span>
            <span className="text-sm text-gray-600">{campaign.treatment_name}</span>
            {campaign.provider_name && (
              <span className="text-sm text-gray-600">With {campaign.provider_name}</span>
            )}
            {campaign.pause_reason && (
              <span className="text-sm text-amber-700">{campaign.pause_reason}</span>
            )}
            {/* The daily cap paces first attempts only; retries keep going
                once it is reached, so this is "new patients", not "calls". */}
            {campaign.daily_new_contact_cap != null && (
              <span className="text-sm text-gray-600">
                {campaign.today_new_contacts ?? 0} / {campaign.daily_new_contact_cap} new patients today
              </span>
            )}
            {campaign.status === "running" && !detail.canDialNow && detail.nextDialAt && (
              <span className="text-sm text-gray-600">
                Next call {formatWireTime(detail.nextDialAt)}
              </span>
            )}
          </div>

          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell className="w-10">#</TableHeaderCell>
                <TableHeaderCell>Patient</TableHeaderCell>
                <TableHeaderCell>Phone</TableHeaderCell>
                <TableHeaderCell>Result</TableHeaderCell>
                <TableHeaderCell>Attempts</TableHeaderCell>
                <TableHeaderCell>Confirmation</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-gray-500">
                    No patients in this campaign.
                  </TableCell>
                </TableRow>
              ) : (
                detail.contacts.map((contact) => {
                  const attempts = contact.attempts || [];
                  return (
                    <TableRow key={contact.id}>
                      <TableCell className="text-sm text-gray-500">{contact.position}</TableCell>
                      <TableCell className="text-sm text-gray-900">
                        {contact.patient_name}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">{contact.phone}</TableCell>
                      <TableCell className="text-sm">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          outcomeClassName(contact)}`}>
                          {outcomeLabel(contact)}
                        </span>
                        {contact.error && (
                          <span className="block text-xs text-gray-500">{contact.error}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        <span>{attemptLabel(contact)}</span>
                        {attempts.length > 0 && (
                          <details className="mt-1">
                            <summary className="cursor-pointer text-xs text-gray-500">
                              {attempts.length} attempt{attempts.length === 1 ? "" : "s"}
                            </summary>
                            <ul className="mt-1 space-y-1">
                              {attempts.map((a, i) => (
                                <li key={a.attempt_no ?? i} className="text-xs text-gray-500">
                                  {SLOT_LABELS[a.slot] || "—"} ·{" "}
                                  {OUTCOME_LABELS[a.outcome] || a.error || "…"}
                                  {a.started_at && (
                                    <span> ({formatWireTime(a.started_at)})</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {SMS_STATUS[contact.sms_status] || ""}
                        {contact.sms_error && (
                          <span className="block text-xs text-gray-500">{contact.sms_error}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      ) : null}
    </SlideOver>
  );
}
